import type { PosthogService } from './PosthogService';
import _ from 'lodash';
import type { AnalyticsOptions } from './ExchangeData';
import type { UserService } from './UserService.svelte';
import type { FirebaseService } from './FirebaseService';
import type CookieConsent from 'vanilla-cookieconsent';

const baseOptions = {
	cookie: {
		useLocalStorage: true
	},
	autoShow: true
} as CookieConsent.CookieConsentConfig;

const webpageOptions = {
	...baseOptions,
	categories: {
		necessary: {
			enabled: true, // this category is enabled by default
			readOnly: true // this category cannot be disabled
		},
		analytics: {
			enabled: true
		}
	},

	language: {
		default: 'en',
		translations: {
			en: {
				consentModal: {
					title: 'Cookies 🍪',
					description: 'We use cookies to help improve <br /> the site experience',
					acceptAllBtn: 'Accept all',
					acceptNecessaryBtn: 'Reject all',
					showPreferencesBtn: 'Manage Individual preferences'
				},
				preferencesModal: {
					title: 'Manage cookie preferences',
					acceptAllBtn: 'Accept all',
					acceptNecessaryBtn: 'Reject all',
					savePreferencesBtn: 'Accept current selection',
					closeIconLabel: 'Close modal',
					sections: [
						{
							title: 'Strictly Necessary cookies',
							description:
								'These cookies are essential for the proper functioning of the website and cannot be disabled.',

							//this field will generate a toggle linked to the 'necessary' category
							linkedCategory: 'necessary'
						},
						{
							title: 'Performance and Analytics',
							description: 'These cookies collect information about how you use our website.',
							linkedCategory: 'analytics'
						},
						{
							title: 'More information',
							description:
								'For any queries in relation to my policy on cookies and your choices, please refer to our <a href="/privacy">privacy policy</a>'
						}
					]
				}
			}
		}
	}
} as CookieConsent.CookieConsentConfig;

const pluginOptions = {
	...baseOptions,
	categories: {
		necessary: {
			enabled: true, // this category is enabled by default
			readOnly: true // this category cannot be disabled
		},
		analytics: {
			enabled: true
		}
	},

	language: {
		default: 'en',
		translations: {
			en: {
				consentModal: {
					title: 'Share Usage Data?',
					description:
						'This data helps improve the <br /> plugin experience and makes <br /> it easier to provide support.',
					acceptAllBtn: 'Accept all',
					acceptNecessaryBtn: 'Reject all',
					showPreferencesBtn: 'Manage Individual preferences'
				},
				preferencesModal: {
					title: 'Manage cookie preferences',
					acceptAllBtn: 'Accept all',
					acceptNecessaryBtn: 'Reject all',
					savePreferencesBtn: 'Accept current selection',
					closeIconLabel: 'Close modal',
					sections: [
						{
							title: 'Strictly Necessary cookies',
							description:
								'These cookies are essential for the proper functioning of the plugin and cannot be disabled.',

							//this field will generate a toggle linked to the 'necessary' category
							linkedCategory: 'necessary'
						},
						{
							title: 'Performance and Analytics',
							description: 'These cookies collect information about how you use the plugin.',
							linkedCategory: 'analytics'
						}
					]
				}
			}
		}
	}
} as CookieConsent.CookieConsentConfig;

export type CookieConsentService = {
	initCookieConsent: () => Promise<void>;
	showCookieConsentAgain: () => Promise<void>;
	consentGiven: () => Promise<boolean>;
	addConsentChangedEventListener: (fn: (options: AnalyticsOptions) => void) => void;
	giveConsent: () => Promise<void>;
};

export const createWebpageCookieConsentService = _.partial(
	createCookieConsentService,
	webpageOptions
);
export const createPluginCookieConsentService = _.partial(
	createCookieConsentService,
	pluginOptions
);

function createCookieConsentService(
	options: CookieConsent.CookieConsentConfig,
	posthogService: PosthogService,
	firebaseService: FirebaseService,
	userService: UserService
): CookieConsentService {
	const onConsentChangedEvents = [] as Array<(options: AnalyticsOptions) => void>;

	async function consentGiven() {
		const CookieConsent = await getCookieConsent();
		return (
			CookieConsent.acceptedCategory('analytics') ||
			(userInOptOutRegion() && cookieConsentNotYetGiven())
		);
	}

	function onConsentChanged() {
		(async () => {
			const consent = await consentGiven();
			await posthogService.updateConsent(consent);
			await firebaseService.updateConsent(consent);
			onConsentChangedEvents.forEach((fn) =>
				fn({ consent, user: userService.getIdentification() ?? undefined })
			);
		})();
	}

	async function cookieConsentNotYetGiven() {
		const CookieConsent = await getCookieConsent();
		return _.isEmpty(CookieConsent.getCookie());
	}

	function userInOptOutRegion() {
		const timeZoneCldr = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (_.startsWith(timeZoneCldr.toLowerCase(), 'europe')) {
			return false;
		}
		return true;
	}

	const getCookieConsent = _.once(async () => {
		const CookieConsent = await import('vanilla-cookieconsent');
		await import('vanilla-cookieconsent/dist/cookieconsent.css');
		return CookieConsent;
	});

	return {
		initCookieConsent: async () => {
			const CookieConsent = await getCookieConsent();
			CookieConsent.run({ ...options, onConsent: onConsentChanged, onChange: onConsentChanged });
			onConsentChanged();
			return;
		},

		async showCookieConsentAgain() {
			const CookieConsent = await getCookieConsent();
			CookieConsent.show(true);
		},

		consentGiven,

		addConsentChangedEventListener(fn: (options: AnalyticsOptions) => void) {
			onConsentChangedEvents.push(fn);
		},

		async giveConsent() {
			const CookieConsent = await getCookieConsent();
			CookieConsent.acceptCategory('analytics');
		}
	};
}
